import { isQueuedRequestMessage } from './agent_chats';
import { apiPost, responseErrorMessage } from './api_v2';
import { clearPendingWidgetGenerationTurn, storeAgentChatWithGenerationTurn } from './widgetGenerationTurns';

const stopRequests = new Map();

export function latestAgentChatRequestMessageId(messages) {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (message?.kind === 'request' && message?.id && !isQueuedRequestMessage(message)) return message.id;
  }

  return null;
}

/**
 * A stop target pairs a chat with the request message that owns its running turn.
 * They travel together so a chat switch can never aim one chat's stop at another
 * chat's turn — the server would reject that, and the visible turn would keep running.
 */
export function agentChatStopTarget(agentChatId, requestMessageId) {
  if (!agentChatId || !requestMessageId) return null;

  return { agentChatId, requestMessageId };
}

/**
 * Which turn a Stop would cancel, given a loaded transcript and whatever the composer
 * last submitted. The transcript is the source of truth once it has caught up; until
 * then the submit response is, because it names the new request before Cable delivers
 * it — and stopping the turn it replaced settles nothing while the UI claims success.
 *
 * `submittedRequest` is `{ agentChatId, pending: true, replacedRequestMessageId }` while a
 * submit is in flight, then `{ agentChatId, requestMessageId }` once the server has named
 * the new request. Everything is checked against the chat it was captured for: a switch
 * leaves one render holding the previous chat's transcript and submission, and pairing
 * either with the new chat id would post a request that chat never had.
 */
export function resolveAgentChatStopTarget({ agentChatId, isTranscriptLoaded = false, messages = [], submittedRequest = null }) {
  if (!agentChatId) return null;

  const loadedMessages = isTranscriptLoaded ? messages : [];
  const loadedRequestMessageId = latestAgentChatRequestMessageId(loadedMessages);
  const submitted = String(submittedRequest?.agentChatId) === String(agentChatId) ? submittedRequest : null;
  const submittedRequestMessageId = submitted?.requestMessageId || null;

  if (submittedRequestMessageId && !loadedMessages.some((message) => message.id === submittedRequestMessageId)) {
    return agentChatStopTarget(agentChatId, submittedRequestMessageId);
  }

  // A submit still in flight, with the transcript showing only the turn it replaces.
  if (submitted?.pending && loadedRequestMessageId === (submitted.replacedRequestMessageId || null)) return null;

  return agentChatStopTarget(agentChatId, loadedRequestMessageId);
}

export function canStopAgentChat(agentChat, stopTarget) {
  if (agentChat?.status !== 'running' || !agentChat?.id || !stopTarget?.requestMessageId) return false;

  return String(stopTarget.agentChatId) === String(agentChat.id);
}

// v2 failures carry `{ errors: { code_key: true }, validations: {...} }`, so the raw
// envelope stringifies to `[object Object]` and buries the reason.
function stopErrorMessage(data) {
  const { errors } = data || {};
  if (typeof errors === 'string' && errors) return errors;

  const codes = errors && typeof errors === 'object' ? Object.keys(errors).filter((code) => errors[code]) : [];
  return responseErrorMessage(data, codes.join(', ') || 'Unable to stop agent chat.');
}

async function performStopAgentChat({ agentChat, requestMessageId, workspaceId }) {
  const response = await apiPost(`/agent_chats/${agentChat.id}/stop`, {
    request_message_id: requestMessageId,
  });
  const data = await response.json();
  const stoppedAgentChat = data.data?.agent_chat;

  if (!response.ok || !data.ok || !stoppedAgentChat) {
    throw new Error(stopErrorMessage(data));
  }

  if (agentChat.kind === 'widget') {
    await storeAgentChatWithGenerationTurn(stoppedAgentChat);
    clearPendingWidgetGenerationTurn(workspaceId, agentChat.id);
  }

  return stoppedAgentChat;
}

export function stopAgentChat({ agentChat, stopTarget, workspaceId }) {
  const requestMessageId = agentChat?.id && String(stopTarget?.agentChatId) === String(agentChat.id) ? stopTarget.requestMessageId : null;
  if (!requestMessageId) return Promise.reject(new Error('A current request message is required to stop an agent chat.'));

  const key = `${agentChat.id}:${requestMessageId}`;
  const existingRequest = stopRequests.get(key);
  if (existingRequest) return existingRequest;

  const request = performStopAgentChat({ agentChat, requestMessageId, workspaceId }).finally(() => stopRequests.delete(key));
  stopRequests.set(key, request);
  return request;
}
